// src/components/layout/Sidebar.tsx — Sidebar del panel admin POW-02
import { useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Users, CreditCard, Package, Ticket,
  BarChart3, FileText, Shield, Settings, ChevronDown,
  ChevronRight, LogOut, Menu, X, UserCheck, Brain, Database,
  Building2,
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { filtrarNav } from '@/utils/navPermisos'
import logoLight from '@/assets/logo-light.png'
import logoDark from '@/assets/logo-dark.png'
import clsx from 'clsx'

interface NavItem {
  to?: string
  icon: React.ElementType
  label: string
  badge?: string | number
  children?: { to: string; label: string }[]
  group?: string
  modulo?: string
  soloSuperadmin?: boolean
}

const NAV: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', modulo: 'dashboard' },
  { to: '/clientes',  icon: Users,           label: 'Clientes', modulo: 'clientes' },
  { to: '/invitados', icon: UserCheck,        label: 'Invitados', modulo: 'invitados' },
  { to: '/pagos',     icon: CreditCard,       label: 'Pagos realizados', modulo: 'pagos' },
  { group: 'Módulo Comercial' } as any,
  { to: '/planes', icon: Package, label: 'Planes y tarifas', modulo: 'planes' },
  { to: '/cupones', icon: Ticket, label: 'Cupones', modulo: 'cupones' },
  { to: '/costo-ia', icon: Brain, label: 'Costo IA', modulo: 'costo_ia' },
  { to: '/procesos-periodo', icon: Database, label: 'Procesos de Período', modulo: 'procesos_periodo' },
  { group: 'Sistema' } as any,
  { to: '/reportes', icon: BarChart3, label: 'Reportes', modulo: 'reportes' },
  { to: '/facturacion', icon: FileText, label: 'Facturación', modulo: 'facturacion' },
  { to: '/usuarios', icon: Users, label: 'Usuarios', modulo: 'usuarios', soloSuperadmin: true },
  { to: '/empresa-emisora', icon: Building2, label: 'Empresa emisora', modulo: 'empresa_emisora' },
  { to: '/roles', icon: Shield, label: 'Roles', modulo: 'roles' },
  { group: 'Configuración' } as any,
  {
    icon: Settings, label: 'Mantenimiento', modulo: 'mantenimiento',
    children: [
      { to: '/mantenimiento/documento-identidad', label: 'Doc. de Identidad' },
      { to: '/mantenimiento/documento-venta', label: 'Documento de Venta' },
      { to: '/mantenimiento/familia-servicios', label: 'Familia de Servicios' },
      { to: '/mantenimiento/impuestos', label: 'Impuestos' },
      { to: '/mantenimiento/forma-pago', label: 'Condición de Pago' },
      { to: '/mantenimiento/medio-pago', label: 'Medio de Pago' },
      { to: '/mantenimiento/monedas', label: 'Monedas' },
      { to: '/mantenimiento/paises', label: 'Países' },
      { to: '/mantenimiento/sectores', label: 'Sectores' },
      { to: '/mantenimiento/series', label: 'Series' },
      { to: '/mantenimiento/servicios', label: 'Servicios' },
      { to: '/mantenimiento/tipo-cambio', label: 'Tipo de Cambio' },
      { to: '/mantenimiento/ubigeo', label: 'Ubigeo' },
      { to: '/mantenimiento/unidad-medida', label: 'Unidad de Medida' },
    ],
  },
]

export default function Sidebar({ isDark }: { isDark?: boolean }) {
  const { user, logout } = useAuthStore()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [maintOpen, setMaintOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Menú visible según permisos (`ver`) del rol del admin autenticado.
  const nav = useMemo(
    () => filtrarNav(NAV, user?.permisos, user?.es_superadmin),
    [user?.permisos, user?.es_superadmin],
  )

  const sidebarContent = (
    <div className="flex flex-col h-full" style={{ background: 'var(--color-surface-800)', borderRight: '1px solid var(--color-border)' }}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 h-[96px]" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <img
          src={isDark ? logoDark : logoLight}
          alt="MIVA"
          className={clsx(
            "object-contain transition-all",
            collapsed
              ? "h-10" : ""
          )}
        />
        <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:block p-1 rounded hover:bg-[var(--color-surface-600)]">
          <Menu size={16} style={{ color: 'var(--color-text-muted)' }} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {nav.map((item, i) => {
          if ('group' in item) {
            return collapsed ? <div key={i} className="my-2 border-t" style={{ borderColor: 'var(--color-border)' }} /> : (
              <div key={i} className="px-2 pt-4 pb-1 text-xs font-semibold uppercase tracking-widest"
                style={{ color: 'var(--color-text-muted)' }}>
                {(item as any).group}
              </div>
            )
          }

          if (item.children) {
            const isActive = item.children.some(c => location.pathname.startsWith(c.to))
            return (
              <div key={i}>
                <button onClick={() => setMaintOpen(!maintOpen)}
                  className={clsx('w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                    isActive ? 'bg-[rgba(59,130,246,0.15)] text-blue-400' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-700)]')}>
                  <item.icon size={17} className="flex-shrink-0" />
                  {!collapsed && <><span className="flex-1 text-left">{item.label}</span>
                    {maintOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</>}
                </button>
                {maintOpen && !collapsed && (
                  <div className="ml-8 mt-0.5 space-y-0.5">
                    {item.children.map(c => (
                      <NavLink key={c.to} to={c.to}
                        className={({ isActive }) => clsx('block px-3 py-2 rounded-lg text-sm',
                          isActive ? 'bg-[rgba(59,130,246,0.2)] text-blue-300' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-700)]')}>
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )
          }

          return (
            <NavLink key={i} to={item.to!}
              className={({ isActive }) => clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                isActive ? 'bg-[rgba(59,130,246,0.15)] text-blue-400 font-medium' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-700)] hover:text-[var(--color-text-primary)]'
              )}>
              <item.icon size={17} className="flex-shrink-0" />
              {!collapsed && <>
                <span className="flex-1">{item.label}</span>
                {item.badge && <span className="badge badge-blue">{item.badge}</span>}
              </>}
            </NavLink>
          )
        })}
      </nav>


    </div>
  )

  return (
    <>
      {/* Desktop sidebar */}
      <aside className={clsx('hidden lg:flex flex-col h-full transition-all duration-300', collapsed ? 'w-16' : 'w-60')}
        style={{ minWidth: collapsed ? '64px' : '240px' }}>
        {sidebarContent}
      </aside>

      {/* Mobile toggle */}
      <button onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg"
        style={{ background: 'var(--color-surface-700)', border: '1px solid var(--color-border)' }}>
        <Menu size={20} style={{ color: 'var(--color-text-primary)' }} />
      </button>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 h-full">
            {sidebarContent}
            <button onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 p-1 rounded" style={{ color: 'var(--color-text-muted)' }}>
              <X size={18} />
            </button>
          </aside>
        </div>
      )}
    </>
  )
}
